import React from 'react';
import { FilterCriteria, QuoteStatus } from '../types';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

interface FilterSidebarProps {
  filters: FilterCriteria;
  onFilterChange: (filters: FilterCriteria) => void;
  isVisible: boolean;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  onFilterChange,
  isVisible,
}) => {
  const statuses: QuoteStatus[] = ['DRAFT', 'OPEN', 'CANCELLED', 'APPROVED'];

  const handleStatusToggle = (status: QuoteStatus) => {
    const newStatuses = filters.statuses.includes(status)
      ? filters.statuses.filter((s) => s !== status)
      : [...filters.statuses, status];
    onFilterChange({ ...filters, statuses: newStatuses });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    onFilterChange({
      ...filters,
      dateRange: {
        ...filters.dateRange,
        [field]: value ? new Date(value) : null,
      },
    });
  };

  const handleReset = () => {
    onFilterChange({
      statuses: ['DRAFT', 'OPEN', 'CANCELLED', 'APPROVED'],
      dateRange: { start: null, end: null },
      customerSearch: '',
      referenceSearch: '',
      valueRange: { min: 0, max: 1000000 },
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
        <Button variant="outline" size="sm" onClick={handleReset}>
          Reset All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-3">Reference</h3>
          <Input
            type="search"
            placeholder="Search by reference..."
            value={filters.referenceSearch}
            onChange={(e) =>
              onFilterChange({ ...filters, referenceSearch: e.target.value })
            }
          />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-3">Customer</h3>
          <Input
            type="search"
            placeholder="Search by customer name..."
            value={filters.customerSearch}
            onChange={(e) =>
              onFilterChange({ ...filters, customerSearch: e.target.value })
            }
          />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-3">Date Range</h3>
          <div className="flex gap-2">
            <Input
              type="date"
              placeholder="Start Date"
              value={
                filters.dateRange.start
                  ? filters.dateRange.start.toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
            />
            <Input
              type="date"
              placeholder="End Date"
              value={
                filters.dateRange.end
                  ? filters.dateRange.end.toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-text-primary mb-3">Status</h3>
          <div className="flex flex-wrap gap-3">
            {statuses.map((status) => (
              <Checkbox
                key={status}
                label={status}
                checked={filters.statuses.includes(status)}
                onChange={() => handleStatusToggle(status)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;